import { useMemo, useState } from "react";
import { Check, ChevronDown, MapPin, Search } from "lucide-react";
import { Sheet } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useStates } from "./api";
import { pickState, useMyState } from "./myState";
import { useAuth } from "@/stores/auth";

/** "📍 Lagos ▾": the state listings are shown for. Auto-detected, changeable, remembered on this device. */
export function LocationChip({ className, compact }: { className?: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const mine = useMyState();
  const profileState = useAuth((s) => s.user?.default_state ?? null);
  const states = useStates();
  const currentId = mine?.id ?? profileState ?? null;
  const name = mine?.name ?? states.data?.find((s) => s.id === currentId)?.name ?? "All Nigeria";
  const short = name === "Federal Capital Territory" ? "Abuja (FCT)" : compact && name === "All Nigeria" ? "Nigeria" : name;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex h-9 min-w-0 max-w-[9.5rem] items-center gap-1 rounded-full border border-line bg-surface px-2.5 text-sm font-medium text-ink transition hover:border-[var(--border-glow)]",
          className,
        )}
        aria-label={`Location: ${name}. Change`}
      >
        <MapPin className="size-4 shrink-0 text-[var(--primary-text)]" aria-hidden />
        <span className={cn("truncate", compact && "max-w-[5.5rem]")}>{short}</span>
        <ChevronDown className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
      </button>
      <StatePicker open={open} onClose={() => setOpen(false)} currentId={currentId} />
    </>
  );
}

function StatePicker({ open, onClose, currentId }: { open: boolean; onClose: () => void; currentId: number | null }) {
  const states = useStates();
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (states.data ?? []).filter((s) => !term || s.name.toLowerCase().includes(term) || s.capital?.toLowerCase().includes(term));
  }, [states.data, q]);
  const choose = (s: { id: number; name: string } | null) => {
    pickState(s);
    setQ("");
    onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title="Where are you shopping?" description="We'll show listings near you first.">
      <div className="flex flex-col gap-3 pb-2">
        <label className="flex h-11 items-center gap-2 rounded-full border border-line bg-surface2 px-4">
          <Search className="size-4 text-ink-muted" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search states"
            aria-label="Search states"
            className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-muted"
          />
        </label>
        <ul className="-mx-2 max-h-[55vh] overflow-y-auto" role="listbox" aria-label="States">
          {!q && <Option label="All Nigeria" hint="Every state" active={currentId === null} onClick={() => choose(null)} />}
          {list.map((s) => (
            <Option
              key={s.id}
              label={s.name}
              hint={s.capital}
              active={s.id === currentId}
              onClick={() => choose({ id: s.id, name: s.name })}
            />
          ))}
        </ul>
      </div>
    </Sheet>
  );
}

function Option({ label, hint, active, onClick }: { label: string; hint?: string; active: boolean; onClick: () => void }) {
  return (
    <li role="option" aria-selected={active}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left hover:bg-fg/5",
          active && "bg-[var(--brand-tint)]",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-medium text-ink">{label}</span>
          {hint && <span className="block text-xs text-ink-muted">{hint}</span>}
        </span>
        {active && <Check className="size-5 text-[var(--primary-text)]" aria-hidden />}
      </button>
    </li>
  );
}
